import { isRiskOverviewAllowedForCompany } from '@/core/constants/risk-overview-embed.constant'
import { useAuthStore } from '@/modules/auth/stores/auth.store'

const defaultOptions = {
  loginRoute: 'AuthLogin',
  unauthorizedRoute: 'Unauthorized',
  homeRoute: 'Home',
  errorHandler: error => console.error('[Auth Guard]:', error),
}

function createAuthGuard(options = {}) {
  const config = { ...defaultOptions, ...options }
  let initializationAttempted = false

  return async (to, from, next) => {
    try {
      const authStore = useAuthStore()

      if (to.name?.includes('Login') && authStore.isAuthenticated) {
        return next({ name: config.homeRoute })
      }

      if (!to.meta.requiresAuth) {
        return next()
      }

      if (!authStore.isAuthenticated && !initializationAttempted) {
        initializationAttempted = true
        try {
          if (!authStore.isUserLoaded) {
            await authStore.fetchUserData()
          }

          if (!authStore.companiesList.length) {
            await authStore.fetchCompaniesList()
          }
        }
        catch {
          if (!authStore.isLoggingOut) {
            await authStore.logout()
          }

          return next({
            name: config.loginRoute,
            query: { redirect: to.fullPath },
          })
        }
      }

      if (!authStore.isAuthenticated) {
        return next({
          name: config.loginRoute,
          query: { redirect: to.fullPath },
        })
      }

      if (to.meta.allowedRoles?.length > 0) {
        const hasRequiredRole = to.meta.allowedRoles.some(role =>
          authStore.userRoles.includes(role),
        )

        if (!hasRequiredRole) {
          return next({
            name: to.meta.fallbackRoute || config.unauthorizedRoute,
            params: to.params,
          })
        }
      }

      if (to.meta.allowedModule) {
        const enabledModules = authStore.tenantData?.enabledModules ?? []
        if (!enabledModules.includes(to.meta.allowedModule)) {
          return next({
            name: to.meta.fallbackRoute || config.unauthorizedRoute,
            params: to.params,
          })
        }
      }

      if (to.meta.riskOverviewCompanyAllowlist) {
        if (!isRiskOverviewAllowedForCompany(authStore.companyData?.id)) {
          return next({ name: config.unauthorizedRoute })
        }
      }

      if (to.meta.adminRole?.length > 0 && to.name === 'tenant-admin-client-modules') {
        const hasRequiredRole = to.meta.adminRole.some(role =>
          authStore.userRoles.includes(role),
        )

        if (!hasRequiredRole) {
          return next({ name: config.unauthorizedRoute })
        }
      }

      if (to.meta.allowedFlag) {
        const flagName = to.meta.allowedFlag
        const tenantHasFlag = Boolean(authStore.tenantData?.[flagName])
        const companyHasFlag = Boolean(authStore.companyData?.[flagName])

        if (!tenantHasFlag && !companyHasFlag) {
          return next({
            name: to.meta.fallbackRoute || config.unauthorizedRoute,
          })
        }
      }

      return next()
    }
    catch (error) {
      config.errorHandler(error)
      return next({
        name: config.loginRoute,
        query: { redirect: to.fullPath },
      })
    }
  }
}

export function createAuthGuardPlugin(options = {}) {
  const guard = createAuthGuard(options)

  return {
    beforeEach: guard,
  }
}
